import { User, UserType } from "../types";
import { batchFetchItems, createCustomToken, CustomAuthoriserData, getUrl } from "./service-utils";
import { userHasPermission } from "../types-helpers";
import { UserInput, UserUpdateValues } from "../../../input-types";

export interface CustomCredentials {
    aws_access_id: string
    aws_secret_key: string
    aws_sts_token: string
    aws_region: string
}

const getUserURL = (
    clientId: string | undefined,
    parameters?: string | undefined,
): URL => {

    return getUrl("users", clientId, parameters)
}

export const dataToUser = (data: any): User | null => {
    if (!data) {
        return null
    }

    if (!data.userId) {
        console.warn("Tried to convert object without id", data)
        return null
    }

    let organisationIds: string[] = []
    if (Array.isArray(data.organisationIds) && data.organisationIds.length > 0) {
        organisationIds = data.organisationIds
    }

    let adminIds: string[] = []
    if (Array.isArray(data.adminIds) && data.adminIds.length > 0) {
        adminIds = data.adminIds
    }

    const user: User = {
        userId: data.userId,
        fullName: data.fullName || "",
        jobTitle: data.jobTitle || "",
        organisationIds,
        adminIds,
        userType: data.userType || UserType.Inactive,
        email: data.email || "",
        lastLogin: data.lastLogin ? new Date(data.lastLogin) : undefined,
        emailPreferences: data.emailPreferences,
        allClientIds: data.allClientIds || data.clientIds,
        clientId: data.clientId,
    };

    return user
}

/**
 * Fetches a single user by their ID
 * @param authToken - Bearer token for authentication
 * @param userId - The ID of the user to fetch
 * @param clientId - The id for the client partition
 * @returns Promise resolving to User object or null if not found
 */
const getUser = async (
    authToken: string,
    userId: string,
    clientId?: string | undefined,
): Promise<User | null> => {
    // Validate input parameters
    if (userId.length === 0) {
        return null;
    }

    const url = getUserURL(clientId, userId)

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        // Handle user not found case
        if (response.status === 404) {
            console.warn("User not found", userId);
            return null;
        }

        if (!response.ok) {
            console.error("Network response was not ok")
            throw new Error("Network response was not ok");
        }

        const data = await response.json();

        const users: (User | null)[] = data.map(dataToUser)

        if (users.length == 0) {
            console.error("Failed to get user!")
            return null
        }

        return users[0]
    } catch (error: unknown) {
        console.error("Error fetching user:", error);
        throw error as Error;
    }
};

/**
 * Fetches multiple users by their IDs with filtering options
 * @param authToken - Bearer token for authentication
 * @param userIds - Array of user IDs to fetch
 * @param showDeleted - Whether to include deleted/inactive users
 * @param clientId - The id for the client partition
 * @returns Promise resolving to array of User objects or null values
 */
const getUserWithIds = async (
    authToken: string,
    userIds: string[],
    clientId?: string | undefined,
): Promise<(User | null)[]> => {

    return batchFetchItems(
        authToken,
        userIds,
        getUserURL,
        dataToUser,
        clientId,
    )
};

/**
 * Fetches all users from the system
 * @param authToken - Bearer token for authentication
 * @param clientId - The id for the client partition
 * @returns Promise resolving to array of all User objects
 */
const listUsers = async (
    authToken: string,
    includeDeleted: boolean,
    clientId?: string | undefined,
): Promise<User[]> => {

    if (clientId != undefined && clientId.length == 0) {
        return []
    }

    const url = getUserURL(clientId)

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(response.status + " Network response was not ok");
        }

        const data = await response.json();

        // Map API response array to User objects
        const userList: User[] = data.map(dataToUser).filter((item: User | null) => item != null);

        if (!includeDeleted) {
            return userList.filter(item => userHasPermission(UserType.Guest, item))
        }

        return userList;
    } catch (error) {
        console.log("Error listing users:", error);
        throw error as Error;
    }
};

/**
 * Updates information about the logged in user
 * @param authToken - Bearer token for authentication
 * @param targetId - Target users id
 * @param input - Updated user values
 * @param clientId - The id for the client partition
 * @returns Promise resolving to response data or null
 */
const updateCurrentUser = async (
    authToken: string,
    targetId: string,
    input: UserUpdateValues,
    clientId?: string | undefined,
): Promise<User | null> => {

    const url = getUserURL(clientId, targetId)

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(input),
        });

        if (!response.ok) {
            if (response.status === 409) {
                // Handle conflict error (e.g., duplicate email)
                return response.json().then((data) => {
                    throw new Error(data.message);
                });
            } else {
                throw new Error(response.status + " Network response was not ok");
            }
        }

        const data = await response.json();

        return dataToUser(data)
    } catch (error) {
        console.log("Error updating user:", error);
        throw error as Error;
    }
};

/**
 * Updates a user's type
 * @param authToken - Bearer token for authentication
 * @param targetId - Target users id
 * @param userType - The user's new type
 * @param clientId - The id for the client partition
 * @returns Promise resolving to response data or null
 */
const updateUserType = async (
    authToken: string,
    targetId: string,
    userType: UserType,
    clientId?: string | undefined,
): Promise<User | null> => {

    const url = getUserURL(clientId, targetId)

    url.searchParams.append("userType", userType)

    try {
        const response = await fetch(url, {
            method: "PUT",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify({
                userType,
            }),
        });

        if (!response.ok) {
            if (response.status === 409) {
                // Handle conflict error (e.g., duplicate email)
                return response.json().then((data) => {
                    throw new Error(data.message);
                });
            } else {
                throw new Error(response.status + " Network response was not ok");
            }
        }

        const data = await response.json();

        return dataToUser(data)
    } catch (error) {
        console.log("Error updating user:", error);
        throw error as Error;
    }
};

/**
 * Admin function to add a new user to the system
 * @param authToken - Bearer token for authentication (admin required)
 * @param user - Input object containing email, userType (guest or user) and optional organisation ids
 * @param clientId - The id for the client partition
 * @returns Promise resolving to response data or null
 */
const inviteUser = async (
    authToken: string,
    user: UserInput,
    clientId?: string | undefined,
): Promise<User | null> => {

    const url = getUserURL(clientId, 'add')

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
            body: JSON.stringify(user),
        })

        const responseJson = await response.json()

        if (!response.ok) {
            throw new Error(responseJson?.message)
        }

        return dataToUser(responseJson);
    } catch (error) {
        console.log("Error adding user:", error);
        throw error as Error;
    }
};

export interface RemoveUserOutput {
    clientIds: string[],
    organisationIds: string[],
}

/**
 * Admin function to delete a user from the system
 * @param authToken - Bearer token for authentication (admin required)
 * @param userId - ID of the user to delete
 * @param clientId - The id for the client partition
 * @returns Promise resolving to response data or null
 */
const removeUserFromClient = async (
    authToken: string,
    targetId: string,
    userType: UserType.Inactive | UserType.Blocked,
    clientId: string,
): Promise<RemoveUserOutput> => {

    const url = getUserURL(clientId, `${targetId}`)

    if (userType) {
        url.searchParams.append("userType", userType)
    }

    try {
        const response = await fetch(url, {
            method: "DELETE",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(response.status + " Network response was not ok");
        }

        const body = await response.json()

        return {
            organisationIds: body?.organisationIds || [],
            clientIds: body?.clientIds || []
        }

    } catch (error) {
        console.log("Error deleting user:", error);
        throw error as Error;
    }
};

/**
 * Admin function to remove block from user (makes them inactive)
 * @param authToken - Bearer token for authentication (admin required)
 * @param userId - ID of the user to unblock
 * @param clientId - The id for the client partition
 * @returns Promise resolving to response data or null
 */
const unBlockUser = async (
    authToken: string,
    targetId: string,
    clientId: string,
): Promise<RemoveUserOutput> => {

    const url = getUserURL(clientId, `${targetId}/block`)

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(response.status + " Network response was not ok");
        }

        const data = await response.json()

        return {
            clientIds: data?.clientIds || [],
            organisationIds: data?.organisationIds || [],
        }
    } catch (error) {
        console.log("Error deleting user:", error);
        throw error as Error;
    }
};

/**
 * Assume role for the current user to get temporary AWS credentials
 * @param authData - Custom authoriser data containing clientId, authToken, and optionally meetingId
 * @returns Promise resolving to custom credentials or null
 */
export const assumeRole = async (
    authData: CustomAuthoriserData
): Promise<CustomCredentials | null> => {

    const url = getUserURL(authData.clientId, `assumeRole`)

    let token = authData.authToken
    if (authData.meetingId) {
        url.searchParams.append("meetingId", authData.meetingId)

        token = createCustomToken(authData) || authData.authToken
    }

    if (!token) {
        console.log("~~Get Meeting join info incomplete parameters")
        return null
    }

    try {
        const response = await fetch(url, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${token}`,
                "Content-Type": "application/json",
            },
        });

        if (!response.ok) {
            throw new Error(response.status + " Network response was not ok");
        }

        const data = await response.json()

        const aws_access_id = data?.aws_access_id || ""
        const aws_secret_key = data?.aws_secret_key || ""
        const aws_sts_token = data?.aws_sts_token || ""
        const aws_region = data?.aws_region || ""

        if (!aws_access_id || !aws_secret_key || !aws_sts_token || !aws_region) {
            console.log("Did not get iot role credentials from response")
            return null
        }

        return {
            aws_access_id,
            aws_secret_key,
            aws_sts_token,
            aws_region,
        }

    } catch (error) {
        console.log("Error assigning IoT permissions:", error);
        throw error as Error;
    }
};

/**
 * Fetches a single user by their ID
 * @param authToken - Bearer token for authentication
 * @param userId - The ID of the user to fetch
 * @param email - The email of the user to resend invite to
 * @param clientId - The id for the client partition
 * @returns Promise resolving to User object or null if not found
 */
const resendInviteLink = async (
    authToken: string,
    input: {
        userId: string,
        email: string,
    },
    clientId?: string | undefined,
): Promise<boolean> => {
    // Validate input parameters
    if (input.userId.length === 0 || input.email.length == 0) {
        return false
    }

    const url = getUserURL(clientId, `${input.userId}/resend`)

    try {
        const response = await fetch(url, {
            method: "POST",
            headers: {
                Authorization: `Bearer ${authToken}`,
                "Content-Type": "application/json",

            },
            body: JSON.stringify(input)
        });

        if (!response.ok) {
            console.error("Network response was not ok")
            throw new Error("Network response was not ok");
        }

        return true
    } catch (error: unknown) {
        console.error("Error fetching user:", error);
        throw error as Error;
    }
};

// Export the main service object with all user-related API functions
const UsersAPIService = {
    getUser,
    listUsers,
    updateCurrentUser,
    updateUserType,
    getUserWithIds,
    inviteUser,
    removeUserFromClient,
    unBlockUser,
    resendInviteLink,
};

export default UsersAPIService;